'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { getSpeakerColor } from '@/lib/speaker-colors';
import { cn } from '@/lib/utils';
import {
  Mic,
  Brain,
  Globe,
  FileText,
  Upload,
  Download,
  Users,
  GraduationCap,
  Briefcase,
  Podcast,
  Phone,
  Scale,
  ArrowRight,
  Radio,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { locales, type Locale, getLocale, setLocale } from '@/i18n/client';
import { useState, useEffect } from 'react';
import LiveTranscriptionDemo from '@/components/LiveTranscriptionDemo';
import { isComingSoonMode } from '@/lib/config';
import GetUpdates from '@/components/GetUpdates';

const languageNames: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
  ru: 'Русский',
  ar: 'العربية',
  hi: 'हिन्दी',
  nl: 'Nederlands',
  pl: 'Polski',
  tr: 'Türkçe',
};

/**
 * Signature Chroma hero visual: a speaker-colored chat-bubble conversation
 * that resolves into an ink "Notiia summary" card. Static (no animation) so it
 * reads instantly; the live/animated variant lives in LiveTranscriptionDemo.
 */
function ConversationDemo() {
  const t = useTranslations();

  const turns = [
    {
      name: t('landing.demo.speaker1Name'),
      time: t('landing.demo.speaker1Time'),
      text: t('landing.demo.speaker1Text'),
    },
    {
      name: t('landing.demo.speaker2Name'),
      time: t('landing.demo.speaker2Time'),
      text: t('landing.demo.speaker2Text'),
    },
    {
      name: t('landing.demo.speaker3Name'),
      time: t('landing.demo.speaker3Time'),
      text: t('landing.demo.speaker3Text'),
    },
  ];

  return (
    <div className="mx-auto grid max-w-2xl gap-3 text-left">
      {turns.map((turn, i) => {
        const c = getSpeakerColor(i);
        return (
          <div key={i} className="grid grid-cols-[34px_1fr] items-start gap-3">
            <span
              className={cn(
                'grid h-[34px] w-[34px] place-items-center rounded-full text-xs font-extrabold text-white',
                c.bgClass
              )}
              aria-hidden="true"
            >
              {turn.name.charAt(0)}
            </span>
            <div
              className={cn(
                'rounded-[4px_16px_16px_16px] border-l-[3px] bg-card px-4 py-2.5 shadow-float',
                c.borderClass
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className={cn('text-[11.5px] font-bold', c.textClass)}>
                  {turn.name}
                </span>
                <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">
                  {turn.time}
                </span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-[hsl(213_20%_28%)] dark:text-foreground/80">
                {turn.text}
              </p>
            </div>
          </div>
        );
      })}

      {/* Notiia summary card */}
      <div className="mt-1 rounded-2xl bg-primary px-5 py-4 text-sm text-primary-foreground">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-speaker-3">
          {t('landing.demo.summaryLabel')}
        </span>
        {t('landing.demo.summaryBody')}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const t = useTranslations();
  const [currentLocale, setCurrentLocale] = useState<Locale>('en');
  const comingSoon = isComingSoonMode();

  useEffect(() => {
    setCurrentLocale(getLocale());
  }, []);

  const handleLanguageChange = (locale: Locale) => {
    setLocale(locale);
  };

  // Feature band — mist cards with colored square dots (speaker hues in order).
  const featureBand = [
    { key: 'voices', speaker: 0 },
    { key: 'live', speaker: 1 },
    { key: 'summaries', speaker: 2 },
    { key: 'languages', speaker: 3 },
  ] as const;

  const features = [
    { icon: Mic, key: 'realtime', speaker: 0 },
    { icon: Brain, key: 'ai', speaker: 1 },
    { icon: Globe, key: 'multilingual', speaker: 2 },
    { icon: FileText, key: 'templates', speaker: 3 },
    { icon: Upload, key: 'upload', speaker: 4 },
    { icon: Download, key: 'export', speaker: 5 },
  ] as const;

  const useCases = [
    { icon: Users, key: 'meetings', speaker: 0 },
    { icon: GraduationCap, key: 'lectures', speaker: 1 },
    { icon: Briefcase, key: 'interviews', speaker: 2 },
    { icon: Podcast, key: 'podcasts', speaker: 3 },
    { icon: Phone, key: 'sales', speaker: 4 },
    { icon: Scale, key: 'legal', speaker: 5 },
  ] as const;

  const steps = [
    { icon: Upload, key: 'step1', speaker: 0 },
    { icon: Brain, key: 'step2', speaker: 1 },
    { icon: Radio, key: 'step3', speaker: 2 },
  ] as const;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="relative flex items-center justify-between">
            {/* Logo - Left */}
            <Link href="/" className="flex flex-shrink-0 items-center">
              <Logo size="md" />
            </Link>

            {/* Main Navigation - Center */}
            <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
              <a
                href="#features"
                className="rounded-full font-medium text-secondary-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t('landing.nav.features')}
              </a>
              <a
                href="#how-it-works"
                className="rounded-full font-medium text-secondary-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t('landing.nav.howItWorks')}
              </a>
              <a
                href="#use-cases"
                className="rounded-full font-medium text-secondary-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t('landing.nav.useCases')}
              </a>
            </div>

            {/* Actions - Right */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Change language">
                    <Globe className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-[400px] overflow-y-auto">
                  {locales.map((locale) => (
                    <DropdownMenuItem
                      key={locale}
                      onClick={() => handleLanguageChange(locale)}
                      className={currentLocale === locale ? 'bg-muted font-medium text-foreground' : ''}
                    >
                      {languageNames[locale]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {!comingSoon && (
                <Link href="/login">
                  <Button variant="ghost">{t('landing.nav.login')}</Button>
                </Link>
              )}
              <Link href="/signup">
                <Button className="group">
                  {comingSoon ? t('comingSoon.submit') : t('landing.hero.cta')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto max-w-4xl px-4 pb-20 pt-16 text-center md:pt-24">
        {/* Eyebrow */}
        <div className="animate-fade-in-up mb-6 inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-1.5">
          <span className="flex items-center gap-[3px]" aria-hidden="true">
            <span className="h-2 w-2 rounded-full bg-speaker-1" />
            <span className="h-2 w-2 rounded-full bg-speaker-2" />
            <span className="h-2 w-2 rounded-full bg-speaker-3" />
          </span>
          <span className="text-xs font-semibold text-secondary-foreground">
            {comingSoon ? t('comingSoon.title') : t('landing.hero.trustedBy')}
          </span>
        </div>

        {/* Multi-color Chroma headline */}
        <h1 className="mx-auto max-w-[22ch] text-balance font-display text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl lg:text-6xl">
          <span className="text-speaker-1">{t('landing.hero.lineWho')}</span>{' '}
          <span className="text-speaker-2">{t('landing.hero.lineWhen')}</span>{' '}
          <span className="text-speaker-3-text">{t('landing.hero.lineLang')}</span>{' '}
          <span className="text-foreground">{t('landing.hero.lineTail')}</span>
        </h1>

        <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-relaxed text-secondary-foreground">
          {comingSoon ? t('comingSoon.description') : t('landing.hero.subtitle')}
        </p>

        {/* CTAs — only when NOT in coming soon mode */}
        {!comingSoon && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link href="/signup">
              <Button size="lg" className="group">
                {t('landing.hero.cta')}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link href="#how-it-works">
              <Button size="lg" variant="outline">
                {t('landing.hero.secondaryCta')}
              </Button>
            </Link>
          </div>
        )}

        {/* Coming Soon form OR signature conversation visual */}
        <div className="animate-fade-in-up mt-14" style={{ animationDelay: '0.15s' }}>
          {comingSoon ? <GetUpdates /> : <ConversationDemo />}
        </div>
      </section>

      {/* Feature band — mist cards on white band with colored square dots */}
      <section className="border-y border-border bg-card">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featureBand.map((item) => {
              const c = getSpeakerColor(item.speaker);
              return (
                <div key={item.key} className="rounded-2xl bg-background p-5">
                  <span
                    className={cn('mb-3 block h-6 w-6 rounded-lg', c.bgClass)}
                    aria-hidden="true"
                  />
                  <h3 className="mb-1 text-sm font-semibold text-foreground">
                    {t(`landing.featureBand.${item.key}.title`)}
                  </h3>
                  <p className="text-[13px] leading-relaxed text-secondary-foreground">
                    {t(`landing.featureBand.${item.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live Demo Section — only in full mode */}
      {!comingSoon && (
        <section className="bg-background py-20">
          <div className="container mx-auto max-w-3xl px-4">
            <LiveTranscriptionDemo />
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="bg-card py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-balance font-display text-3xl font-bold tracking-tight md:text-4xl">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg leading-relaxed text-secondary-foreground">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => {
              const Icon = feature.icon;
              const c = getSpeakerColor(feature.speaker);
              return (
                <div
                  key={feature.key}
                  className="group rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:shadow-float"
                >
                  <div
                    className={cn(
                      'mb-6 flex h-12 w-12 items-center justify-center rounded-xl text-white transition-transform group-hover:scale-105',
                      c.bgClass
                    )}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mb-3 text-xl font-semibold tracking-tight">
                    {t(`landing.features.${feature.key}.title`)}
                  </h3>
                  <p className="leading-relaxed text-secondary-foreground">
                    {t(`landing.features.${feature.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-background py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-balance font-display text-3xl font-bold tracking-tight md:text-4xl">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-lg leading-relaxed text-secondary-foreground">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid gap-12 md:grid-cols-3">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const c = getSpeakerColor(step.speaker);
              return (
                <div key={step.key} className="relative text-center">
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-[60%] top-8 hidden h-px w-[80%] bg-border md:block" />
                  )}

                  <div
                    className={cn(
                      'relative mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-float',
                      c.bgClass
                    )}
                  >
                    <Icon className="h-7 w-7" />
                    <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-primary font-mono text-[11px] font-bold tabular-nums text-primary-foreground">
                      {index + 1}
                    </span>
                  </div>

                  <h3 className="mb-3 text-xl font-semibold tracking-tight">
                    {t(`landing.howItWorks.${step.key}.title`)}
                  </h3>
                  <p className="leading-relaxed text-secondary-foreground">
                    {t(`landing.howItWorks.${step.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="bg-card py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-balance font-display text-3xl font-bold tracking-tight md:text-4xl">
              {t('landing.useCases.title')}
            </h2>
            <p className="text-lg leading-relaxed text-secondary-foreground">
              {t('landing.useCases.subtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => {
              const Icon = useCase.icon;
              const c = getSpeakerColor(useCase.speaker);
              return (
                <div
                  key={useCase.key}
                  className="group rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:shadow-float"
                >
                  <div
                    className={cn(
                      'mb-5 flex h-11 w-11 items-center justify-center rounded-xl',
                      c.bgClass
                    )}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="mb-3 text-lg font-semibold tracking-tight">
                    {t(`landing.useCases.${useCase.key}.title`)}
                  </h3>
                  <p className="leading-relaxed text-secondary-foreground">
                    {t(`landing.useCases.${useCase.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section — ink band */}
      <section className="bg-primary py-24 text-primary-foreground">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h2 className="mb-4 text-balance font-display text-3xl font-bold tracking-tight md:text-4xl">
            {comingSoon ? t('comingSoon.title') : t('landing.cta.title')}
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-primary-foreground/75">
            {comingSoon ? t('comingSoon.description') : t('landing.cta.subtitle')}
          </p>

          {comingSoon ? (
            <div className="mx-auto max-w-md">
              <GetUpdates />
            </div>
          ) : (
            <div className="space-y-4">
              <Link href="/signup">
                <Button
                  size="lg"
                  className="group bg-card text-foreground hover:bg-card/90"
                >
                  {t('landing.cta.button')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <p className="text-sm text-primary-foreground/60">{t('landing.cta.noCredit')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mb-12 grid gap-12 md:grid-cols-4">
            <div>
              <div className="mb-5">
                <Logo size="md" />
              </div>
              <p className="text-sm leading-relaxed text-secondary-foreground">
                {t('landing.footer.description')}
              </p>
            </div>

            <div>
              <h4 className="mb-5 text-base font-semibold text-foreground">
                {t('landing.footer.product')}
              </h4>
              <ul className="space-y-3 text-sm text-secondary-foreground">
                <li><a href="#features" className="transition-colors hover:text-foreground">{t('landing.footer.features')}</a></li>
                <li><a href="/pricing" className="transition-colors hover:text-foreground">{t('landing.footer.pricing')}</a></li>
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.api')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-5 text-base font-semibold text-foreground">
                {t('landing.footer.company')}
              </h4>
              <ul className="space-y-3 text-sm text-secondary-foreground">
                <li><a href="/about" className="transition-colors hover:text-foreground">{t('landing.footer.about')}</a></li>
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.blog')}</a></li>
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.careers')}</a></li>
                <li><a href="/contact" className="transition-colors hover:text-foreground">{t('landing.footer.contact')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-5 text-base font-semibold text-foreground">
                {t('landing.footer.support')}
              </h4>
              <ul className="space-y-3 text-sm text-secondary-foreground">
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.docs')}</a></li>
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.help')}</a></li>
                <li><a href="#" className="transition-colors hover:text-foreground">{t('landing.footer.status')}</a></li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-8 md:flex-row">
            <p className="text-sm text-secondary-foreground">{t('landing.footer.copyright')}</p>
            <div className="flex gap-8 text-sm text-secondary-foreground">
              <Link href="/privacy-policy" className="transition-colors hover:text-foreground">{t('landing.footer.privacy')}</Link>
              <Link href="/terms-of-service" className="transition-colors hover:text-foreground">{t('landing.footer.terms')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
